/**
 * EnrichmentProvider — the contract every calendar provider implements so the
 * useEventTitles hook can stay provider-agnostic.
 *
 * See docs/calendar-cache-privacy-plan.md (P3, P4) for the design rationale.
 *
 * Implementations to date:
 *   - GoogleCalendarEnrichmentProvider (web/src/features/calendar-enrichment/providers/google.js)
 *
 * Future, scaffolded but not implemented:
 *   - AppleCalendarEnrichmentProvider (CalDAV via iCloud)
 *   - CalDavEnrichmentProvider (generic CalDAV servers)
 *   - ICalSubscriptionEnrichmentProvider (read-only .ics feeds)
 *
 * Per-provider quirks (per-event vs per-feed fetch granularity, auth shape)
 * live behind this interface — the hook above only ever sees the uniform
 * Promise<Map<eventId, EnrichmentResult>> shape.
 */

/**
 * @typedef {Object} EnrichmentRequest
 * @property {string} provider     — provider id ('google' | 'apple' | 'caldav' | 'ical')
 * @property {string} calendarId   — provider-scoped calendar identifier
 * @property {string} eventId      — provider-scoped event identifier
 */

/**
 * @typedef {Object} EnrichmentResult
 * @property {string|null} title       — resolved title, or null when not yet fetched
 * @property {number} fetchedAt        — Date.now() at fetch time (used for TTL)
 * @property {EnrichmentErrorCode} [error] — present iff fetch failed
 */

/**
 * @typedef {'permission_denied' | 'not_found' | 'rate_limited' | 'network'} EnrichmentErrorCode
 *
 * permission_denied — token expired, scope missing, calendar unlinked. UI should
 *                     surface a one-time "re-link calendar" toast (this is the
 *                     load-bearing distinguishable error state from the plan).
 * not_found         — the event was deleted between sync and render.
 * rate_limited      — Google's rate limiter tripped; retry-with-backoff at the hook layer.
 * network           — fetch threw or got a 5xx; transient, will retry on next access.
 */

/**
 * @typedef {Object} ProviderCredentials
 * Generic credential shape. Each provider narrows it (e.g. Google: { accessToken, expiresAt }).
 */

/**
 * @typedef {Object} EnrichmentProvider
 *
 * @property {string} providerId
 *
 * @property {(args: {
 *   calendarId: string,
 *   eventIds: string[],
 *   credentials: ProviderCredentials,
 *   signal?: AbortSignal,
 * }) => Promise<Map<string, EnrichmentResult>>} enrichByIds
 *   Lookup titles for a sparse set of (calendarId, eventId) pairs. Used when
 *   the consumer knows the exact events it needs (e.g. HideEventDialog
 *   resolving the title of the event the user clicked). Implementations
 *   that lack a per-event API (iCal subscriptions) MAY delegate to
 *   enrichByRange under the hood with a wide window + post-filter.
 *
 * @property {(args: {
 *   calendarId: string,
 *   timeMin: string,
 *   timeMax: string,
 *   credentials: ProviderCredentials,
 *   signal?: AbortSignal,
 * }) => Promise<Map<string, EnrichmentResult>>} enrichByRange
 *   Lookup titles for every event in [timeMin, timeMax] for one calendar.
 *   Used when the consumer is rendering a calendar view and wants to batch
 *   the whole window in a single network call. Implementations that lack a
 *   range-fetch API SHOULD return the empty map; the hook will fall back to
 *   per-id enrichment.
 */

/**
 * Validate that an arbitrary object satisfies the EnrichmentProvider contract.
 * Used by the test helper assertProviderImplementsInterface and at provider
 * registration time to fail loudly when an implementation drifts.
 *
 * @param {unknown} provider
 * @returns {{ ok: boolean, errors: string[] }}
 */
export function validateEnrichmentProvider(provider) {
  const errors = [];
  if (!provider || typeof provider !== "object") {
    return { ok: false, errors: ["provider must be an object"] };
  }
  if (typeof provider.providerId !== "string" || !provider.providerId) {
    errors.push("provider.providerId must be a non-empty string");
  }
  if (typeof provider.enrichByIds !== "function") {
    errors.push("provider.enrichByIds must be a function");
  }
  if (typeof provider.enrichByRange !== "function") {
    errors.push("provider.enrichByRange must be a function");
  }
  return { ok: errors.length === 0, errors };
}

/**
 * Compound key used by the hook's in-memory cache. Stable for the same
 * (provider, calendarId, eventId) triple regardless of doc-id hashing.
 *
 * Note: this is purely a client-side cache key. The Firestore doc id is
 * computed differently (via SHA-1) on the server side; we don't need a
 * cryptographic key here, just a delimiter unlikely to appear in Google's
 * id strings.
 */
export function compoundEventKey(provider, calendarId, eventId) {
  return `${provider}|${calendarId}|${eventId}`;
}

// ---------------------------------------------------------------------------
// Provider credential shapes (calendar-cache-privacy P4)
// ---------------------------------------------------------------------------
//
// The persisted `linkedCalendars/{calendarId}.providerCredentials` blob is
// keyed by provider id. Each provider has a different required field set
// because the underlying auth model differs:
//   - google: OAuth2 → store the long-lived refresh token; mint short-lived
//             access tokens server-side (`googleCalendarMintAccessToken`).
//   - apple:  app-specific password issued from the user's Apple ID.
//   - caldav: HTTP basic auth against the user's chosen CalDAV server.
//   - ical:   public/static URL; usually no auth at all.
//
// These validators run BEFORE writing to Firestore. They guarantee the
// credential blob has the fields downstream code (the OAuth refresh path,
// the CalDAV REPORT request, etc.) actually needs.
//
// **Stubs:** Apple, CalDAV, and iCal validators are stubs — there is no
// runtime provider implementation that consumes them yet. They exist so
// adding a real provider later is a strictly additive change (write the
// provider, validators are already there). Tests cover the happy + missing
// + wrong-type cases for all four shapes regardless.
//
// @typedef {Object} GooglePersistedCredentials
// @property {string} refreshToken       Long-lived OAuth refresh token. Server-side only.
// @property {string[]} [scopes]         Granted scopes (e.g. ['calendar.readonly']).
// @property {number} [tokenExpiry]      Last-known access-token expiry epoch ms.
//
// @typedef {Object} ApplePersistedCredentials
// @property {string} appleId            User's Apple ID.
// @property {string} appSpecificPassword  Generated at appleid.apple.com.
//
// @typedef {Object} CalDavPersistedCredentials
// @property {string} url                CalDAV server URL.
// @property {string} username
// @property {string} passwordRef        Reference to the secret-store entry holding the password.
//
// @typedef {Object} ICalPersistedCredentials
// @property {string} feedUrl            Public/auth'd .ics feed URL.

/**
 * @param {unknown} credentials
 * @returns {{ ok: boolean, errors: string[] }}
 */
export function validateGoogleCredentials(credentials) {
  const errors = collectShapeErrors(credentials, "google");
  if (errors.length) return { ok: false, errors };
  if (typeof credentials.refreshToken !== "string" || !credentials.refreshToken) {
    errors.push("google.refreshToken must be a non-empty string");
  }
  if (
    credentials.scopes !== undefined &&
    !(
      Array.isArray(credentials.scopes) &&
      credentials.scopes.every((s) => typeof s === "string")
    )
  ) {
    errors.push("google.scopes (when present) must be an array of strings");
  }
  if (
    credentials.tokenExpiry !== undefined &&
    typeof credentials.tokenExpiry !== "number"
  ) {
    errors.push("google.tokenExpiry (when present) must be a number (epoch ms)");
  }
  return { ok: errors.length === 0, errors };
}

/**
 * @param {unknown} credentials
 * @returns {{ ok: boolean, errors: string[] }}
 */
export function validateAppleCredentials(credentials) {
  const errors = collectShapeErrors(credentials, "apple");
  if (errors.length) return { ok: false, errors };
  if (typeof credentials.appleId !== "string" || !credentials.appleId) {
    errors.push("apple.appleId must be a non-empty string");
  }
  if (
    typeof credentials.appSpecificPassword !== "string" ||
    !credentials.appSpecificPassword
  ) {
    errors.push("apple.appSpecificPassword must be a non-empty string");
  }
  return { ok: errors.length === 0, errors };
}

/**
 * @param {unknown} credentials
 * @returns {{ ok: boolean, errors: string[] }}
 */
export function validateCalDavCredentials(credentials) {
  const errors = collectShapeErrors(credentials, "caldav");
  if (errors.length) return { ok: false, errors };
  if (typeof credentials.url !== "string" || !credentials.url) {
    errors.push("caldav.url must be a non-empty string");
  }
  if (typeof credentials.username !== "string" || !credentials.username) {
    errors.push("caldav.username must be a non-empty string");
  }
  if (typeof credentials.passwordRef !== "string" || !credentials.passwordRef) {
    errors.push("caldav.passwordRef must be a non-empty string");
  }
  return { ok: errors.length === 0, errors };
}

/**
 * @param {unknown} credentials
 * @returns {{ ok: boolean, errors: string[] }}
 */
export function validateICalCredentials(credentials) {
  const errors = collectShapeErrors(credentials, "ical");
  if (errors.length) return { ok: false, errors };
  if (typeof credentials.feedUrl !== "string" || !credentials.feedUrl) {
    errors.push("ical.feedUrl must be a non-empty string");
  }
  return { ok: errors.length === 0, errors };
}

const CREDENTIAL_VALIDATORS = {
  google: validateGoogleCredentials,
  apple: validateAppleCredentials,
  caldav: validateCalDavCredentials,
  ical: validateICalCredentials,
};

/**
 * Look up the validator for a given provider id. Returns null when unknown
 * — caller decides whether unknown providers are an error.
 *
 * @param {string} providerId
 * @returns {((c: unknown) => {ok: boolean, errors: string[]}) | null}
 */
export function pickCredentialValidator(providerId) {
  return CREDENTIAL_VALIDATORS[providerId] || null;
}

function collectShapeErrors(credentials, providerId) {
  if (credentials == null) {
    return [`${providerId} credentials must be an object (got ${credentials})`];
  }
  if (typeof credentials !== "object" || Array.isArray(credentials)) {
    return [`${providerId} credentials must be a plain object`];
  }
  return [];
}

// ---------------------------------------------------------------------------
// Interface contract assertion (calendar-cache-privacy P4)
// ---------------------------------------------------------------------------

/**
 * Async assertion helper: throws if the given provider does not satisfy the
 * EnrichmentProvider contract. Use this in unit tests for any new provider
 * implementation — every provider gets the contract coverage for free, so
 * adding a future Apple/CalDAV/iCal provider is a strictly additive change.
 *
 * What it asserts:
 *   1. Static shape is well-formed (validateEnrichmentProvider passes).
 *   2. enrichByIds with empty eventIds returns an empty Map (no provider
 *      should make a network round-trip for nothing).
 *   3. enrichByIds with at least one id returns a Map (entries may carry
 *      results OR error tags — either is conformant).
 *   4. enrichByRange returns a Map even when called with a degenerate
 *      (zero-width) window. Implementations that lack a range API may
 *      legitimately return an empty Map; that's OK.
 *   5. The returned Map values, when present, match the EnrichmentResult
 *      shape: `{ title: string|null, fetchedAt: number, error?: string }`.
 *
 * The helper does NOT make any network calls itself — it relies on the
 * caller passing a provider that's been wired with a stub/mock fetch. For
 * the real Google provider, see google.test.js.
 *
 * @param {EnrichmentProvider} provider
 * @param {{
 *   sampleCalendarId?: string,
 *   sampleEventIds?: string[],
 *   sampleCredentials?: object,
 * }} [opts]
 * @returns {Promise<void>}  Throws on contract violation.
 */
export async function assertProviderImplementsInterface(provider, opts = {}) {
  const { ok, errors } = validateEnrichmentProvider(provider);
  if (!ok) {
    throw new Error(
      `assertProviderImplementsInterface: shape errors:\n  ${errors.join("\n  ")}`,
    );
  }

  const calendarId = opts.sampleCalendarId || "contract-test-calendar";
  const eventIds = opts.sampleEventIds || ["contract-test-evt-1"];
  const credentials = opts.sampleCredentials || {};

  // (2) empty input → empty Map (and ideally no fetch — the implementation
  // is responsible for short-circuiting; we just verify the return shape).
  const empty = await provider.enrichByIds({
    calendarId,
    eventIds: [],
    credentials,
  });
  if (!(empty instanceof Map)) {
    throw new Error(
      "assertProviderImplementsInterface: enrichByIds([]) must return a Map",
    );
  }
  if (empty.size !== 0) {
    throw new Error(
      `assertProviderImplementsInterface: enrichByIds([]) must return an empty Map (got size ${empty.size})`,
    );
  }

  // (3) non-empty input → Map (entries may be results or error-tagged).
  const populated = await provider.enrichByIds({
    calendarId,
    eventIds,
    credentials,
  });
  if (!(populated instanceof Map)) {
    throw new Error(
      "assertProviderImplementsInterface: enrichByIds(non-empty) must return a Map",
    );
  }
  for (const [key, value] of populated) {
    assertEnrichmentResultShape(value, `enrichByIds entry '${key}'`);
  }

  // (4) range fetch returns a Map (possibly empty).
  const ranged = await provider.enrichByRange({
    calendarId,
    timeMin: "2026-01-01T00:00:00.000Z",
    timeMax: "2026-01-01T00:00:00.000Z",
    credentials,
  });
  if (!(ranged instanceof Map)) {
    throw new Error(
      "assertProviderImplementsInterface: enrichByRange(...) must return a Map",
    );
  }
  for (const [key, value] of ranged) {
    assertEnrichmentResultShape(value, `enrichByRange entry '${key}'`);
  }
}

function assertEnrichmentResultShape(value, label) {
  if (value == null || typeof value !== "object") {
    throw new Error(`${label}: EnrichmentResult must be an object`);
  }
  if (!("title" in value)) {
    throw new Error(`${label}: EnrichmentResult must have a 'title' field`);
  }
  if (value.title !== null && typeof value.title !== "string") {
    throw new Error(`${label}: EnrichmentResult.title must be a string or null`);
  }
  if (typeof value.fetchedAt !== "number") {
    throw new Error(`${label}: EnrichmentResult.fetchedAt must be a number`);
  }
  if (
    value.error !== undefined &&
    !["permission_denied", "not_found", "rate_limited", "network"].includes(
      value.error,
    )
  ) {
    throw new Error(
      `${label}: EnrichmentResult.error (when present) must be one of permission_denied|not_found|rate_limited|network`,
    );
  }
}
